import React, { useContext, useEffect, useState } from "react";
import { CSVDownload } from "react-csv";
import dayjs from "dayjs";

import {
  getCustomSearchClient,
  getSearchConfigureParams,
  prepareQuery,
} from "utils/helpers/search";
import {
  pickDisplayRate,
  prepareHourlyRate,
} from "./UserCardList/helpers/userCard";

import { SearchContext } from "context/providers";
import SvgButton from "./base/SvgButton";
import SvgIcon from "./base/SvgIcon";
import { cn } from "utils";

const prepareLocation = (location) => {
  if (location?.countryName) {
    return location.countryName;
  }

  return "";
};

const prepareAvailability = (availability) => {
  if (!availability) return "";
  return availability;
};

const prepareLangs = (langs) => {
  if (!langs || !langs?.length) {
    return "Not Specified";
  }

  const preparedArray = langs.map(
    (lang) =>
      `${lang.language} ${
        lang.level
          ? `(${lang.level?.[0]}${lang.level?.slice(1)?.toLowerCase()})`
          : ""
      }`
  );

  return preparedArray.join(", ");
};

const prepareData = (data) => {
  return data.map((h) => ({
    username: h.username,
    email: h.email,
    given_name: h.given_name,
    family_name: h.family_name,
    location: prepareLocation(h.location),
    availability: prepareAvailability(h?.availability),
    ratePerHour: prepareHourlyRate(pickDisplayRate(h)),
    knownLanguages: prepareLangs(h.knownLanguages),
    createdAt: dayjs(h.createdAt).format("MM/DD/YYYY"),
  }));
};

export default function DownloadResults({
  className,
  data,
  tooltip,
  label,
  loadingLabel = "Preparing download...",
  hideIcon = false,
}) {
  const { searchState, operators } = useContext(SearchContext);
  const [preparingCSV, setPreparingCSV] = useState(false);
  const [exportCSV, setExportCSV] = useState(null);

  useEffect(() => {
    if (data && exportCSV) {
      setExportCSV(null);
    }
  }, [data, exportCSV]);

  const exportAsCSV = async () => {
    const filters = prepareQuery(searchState, operators);
    const platformUsersIndexName = process.env.REACT_APP_OS_USERS_INDEX_NAME;
    const searchConfigureParams = getSearchConfigureParams(
      searchState,
      operators,
      platformUsersIndexName
    );

    setPreparingCSV(true);

    try {
      const results = [];
      let indexing = true;
      let page = 0;
      while (indexing) {
        const data = await getCustomSearchClient(undefined, undefined, {
          platformUsersIndexName,
        }).search([
          {
            indexName: platformUsersIndexName,
            query: searchConfigureParams.query,
            params: {
              ...filters,
              ...searchConfigureParams,
              facets: ["*"],
              attributesToSnippet: [],
              attributesToRetrieve: [
                "username",
                "email",
                "given_name",
                "family_name",
                "location",
                "availability",
                "ratePerHour",
                "knownLanguages",
                "createdAt",
              ],
              hitsPerPage: 10000,
              page,
            },
          },
        ]);

        if (
          data?.results[0]?.hits?.length === 0 ||
          data?.results[0]?.nbPages - 1 === page
        ) {
          indexing = false;
        }

        results.push(...data.results[0].hits);
        page++;
      }

      setExportCSV(prepareData(results));

      setPreparingCSV(false);
    } catch (e) {
      console.log(e);
      alert("An error occurred preparing the data for download. Try again.");
      setExportCSV(null);
      setPreparingCSV(false);
    }
  };

  const handleExportClick = async () => {
    if (data) {
      // use provided data
      setExportCSV(prepareData(data));
    } else {
      // download data
      await exportAsCSV();
    }
  };

  return (
    <>
      {label ? (
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 text-sm text-sky-600 underline underline-offset-2 transition-opacity hover:no-underline disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          title={tooltip}
          onClick={handleExportClick}
          disabled={preparingCSV}
        >
          {!hideIcon && (
            <SvgIcon type="downloadFile" className="h-4 w-4 shrink-0" />
          )}
          <span>{preparingCSV ? loadingLabel : label}</span>
        </button>
      ) : (
        <SvgButton
          icon="downloadFile"
          className={cn(className, preparingCSV ? "animate-bounce" : "")}
          tooltip={tooltip}
          onClick={handleExportClick}
          disabled={preparingCSV}
        />
      )}
      {!preparingCSV && exportCSV && (
        <CSVDownload data={exportCSV} target="_blank" />
      )}
    </>
  );
}
